import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Routes, Route } from 'react-router-dom';
import './App.css';
import StarsBackground from './components/StarsBackground';
import AuthModal from './components/AuthModal';
import PaymentModal from './components/PaymentModal';
import AdEditor from './components/AdEditor';
import DemoPage from './components/DemoPage';
import { TypeAnimation } from 'react-type-animation';
import { useAuth0 } from '@auth0/auth0-react';
import JSZip from 'jszip';
import { userDataService, uploadImageToStorage } from './supabaseClient';

// Aris Logo Component
const ArisLogo = () => (
  <img src="/aris_logo.png" alt="Aris Logo" className="logo" />
);

// Main App Component
function MainApp() {
  const { user: auth0User, isAuthenticated, isLoading, getIdTokenClaims, error } = useAuth0();
  
  // Log any Auth0 errors
  useEffect(() => {
    if (error) {
      console.error('❌ [Auth0] Error:', error);
    }
  }, [error]);
  const [user, setUser] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedAds, setGeneratedAds] = useState([]);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [totalGenerations, setTotalGenerations] = useState(0);
  const [userPlan, setUserPlan] = useState(null);
  const [editingAd, setEditingAd] = useState(null);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [isScrapingUrl, setIsScrapingUrl] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [recentGeneratedAds, setRecentGeneratedAds] = useState([]);
  const [editHistories, setEditHistories] = useState({}); // Store edit history per ad ID
  const [viewMode, setViewMode] = useState('home'); // 'home' or 'myads'
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [sliderPosition, setSliderPosition] = useState(0);
  const [adsToShow, setAdsToShow] = useState(16); // Pagination for My Ads
  const selectedAspectRatio = '1:1'; // Default to square aspect ratio
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Progressive loading messages
  const LOADING_MESSAGES = [
    'Analyzing designs',
    'Processing images',
    'Generating layouts',
    'Rendering visuals',
    'Optimizing composition',
    'Applying effects',
    'Enhancing quality',
    'Finalizing assets',
    'Compiling results',
    'Almost ready'
  ];

  const ASPECT_RATIOS = {
    '1:1': { label: 'Square (1:1)', width: 1024, height: 1024, description: 'Perfect for Instagram posts' },
    '9:16': { label: 'Vertical (9:16)', width: 576, height: 1024, description: 'Instagram Stories, TikTok' },
    '16:9': { label: 'Horizontal (16:9)', width: 1024, height: 576, description: 'YouTube thumbnails' },
    '4:5': { label: 'Portrait (4:5)', width: 819, height: 1024, description: 'Instagram portrait' },
    '1.91:1': { label: 'Wide (1.91:1)', width: 1024, height: 536, description: 'Facebook cover, LinkedIn' }
  };

  const FREE_LIMIT = 8; // 8 free generations, then upgrade required
  
  const PLANS = useMemo(() => ({
    starter: { id: 'starter', name: 'Starter', price: 11.99, generations: 200 },
    pro: { id: 'pro', name: 'Pro', price: 24.99, generations: 450 },
    business: { id: 'business', name: 'Business', price: 99.99, generations: 2000 }
  }), []);

  // Sync Auth0 authentication with local state and load from Supabase
  useEffect(() => {
    const loadUserData = async () => {
      console.log('🔐 [Auth] isLoading:', isLoading);
      console.log('🔐 [Auth] isAuthenticated:', isAuthenticated);
      console.log('👤 [Auth] auth0User:', auth0User);
      console.log('🔗 [Auth] Current URL:', window.location.href);
      
      if (isAuthenticated && auth0User) {
        const userId = auth0User.sub;
        console.log('✅ [Auth] User authenticated:', {
          userId,
          email: auth0User.email,
          name: auth0User.name,
          picture: auth0User.picture,
          sub: auth0User.sub
        });
        
        setUser({
          id: userId,
          name: auth0User.name || auth0User.email,
          email: auth0User.email,
          image: auth0User.picture
        });
        
        console.log('📊 [Supabase] Loading user data for:', userId);
        
        // Load user data from Supabase
        const userData = await userDataService.getUserData(userId);
        
        if (userData) {
          console.log('✅ [Supabase] User data loaded:', userData);
          
          // Set generation count
          setTotalGenerations(userData.generation_count || 0);
          
          // Set subscription if active
          if (userData.subscription_plan) {
            const plan = PLANS[userData.subscription_plan];
            if (plan) {
              setUserPlan(plan);
              console.log('💳 [Supabase] Active subscription:', plan.name);
            }
          }
        } else {
          // First time user - create record in Supabase
          console.log('🆕 [Supabase] Creating new user record');
          
          await userDataService.upsertUserData({
            user_id: userId,
            email: auth0User.email,
            generation_count: 0,
            subscription_plan: null,
            generation_limit: 8
          });
          
          setTotalGenerations(0);
        }
        
        // Load user's ad history
        console.log('📜 [Supabase] Loading ad history for user:', userId);
        const adHistory = await userDataService.getUserAdHistory(userId);
        console.log('✅ [Supabase] Loaded', adHistory.length, 'ads from history');
        setGeneratedAds(adHistory);
        
        // Reconstruct edit histories from loaded data
        const reconstructedHistories = {};
        
        // Group edits by their parent
        const editsByParent = {};
        adHistory.forEach(ad => {
          if (ad.parentAdId) {
            if (!editsByParent[ad.parentAdId]) {
              editsByParent[ad.parentAdId] = [];
            }
            editsByParent[ad.parentAdId].push(ad);
          }
        });
        
        // Build edit history for each parent
        Object.keys(editsByParent).forEach(parentId => {
          const originalAd = adHistory.find(a => a.id === parentId);
          if (originalAd) {
            reconstructedHistories[parentId] = [
              {
                id: originalAd.id,
                imageUrl: originalAd.imageUrl,
                prompt: originalAd.prompt,
                isOriginal: true
              }
            ];
            
            // Sort edits by creation date and add them
            const sortedEdits = editsByParent[parentId].sort((a, b) => 
              new Date(a.createdAt) - new Date(b.createdAt)
            );
            
            sortedEdits.forEach(edit => {
              reconstructedHistories[parentId].push({
                id: edit.id,
                imageUrl: edit.imageUrl,
                prompt: edit.prompt,
                isOriginal: false
              });
            });
          }
        });
        
        console.log('🔄 [Edit History] Reconstructed', Object.keys(reconstructedHistories).length, 'edit sessions');
        setEditHistories(reconstructedHistories);
        
        setShowAuthModal(false);
      } else {
        setUser(null);
        setTotalGenerations(0);
        setUserPlan(null);
        setGeneratedAds([]);
      }
    };
    
    loadUserData();
  }, [isAuthenticated, auth0User, PLANS, isLoading]);

  // Save generation count to Supabase whenever it changes
  useEffect(() => {
    const saveGenerationCount = async () => {
      if (user?.id && totalGenerations > 0) {
        console.log('💾 [Supabase] Saving generation count:', totalGenerations);
        
        const success = await userDataService.updateGenerationCount(user.id, totalGenerations);
        
        if (success) {
          console.log('✅ [Supabase] Generation count saved');
        } else {
          console.error('❌ [Supabase] Failed to save generation count');
        }
      }
    };
    
    saveGenerationCount();
  }, [totalGenerations, user]);

  // Cycle through loading messages every 5 seconds
  useEffect(() => {
    if (isGenerating && !isScrapingUrl) {
      setLoadingMessageIndex(0); // Reset to first message
      const interval = setInterval(() => {
        setLoadingMessageIndex(prev => (prev + 1) % LOADING_MESSAGES.length);
      }, 5000); // Change every 5 seconds
      
      return () => clearInterval(interval);
    }
  }, [isGenerating, isScrapingUrl, LOADING_MESSAGES.length]);

  // Handle return from Stripe payment
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    
    if (paymentStatus === 'success' && user?.id) {
      // Clear URL param
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Reload user data after webhook processes (2s delay)
      setTimeout(async () => {
        const userData = await userDataService.getUserData(user.id);
        if (userData?.subscription_plan) {
          const plan = PLANS[userData.subscription_plan];
          if (plan) {
            setUserPlan(plan);
            setTotalGenerations(userData.generation_count || 0);
          }
        }
      }, 2000);
    }
  }, [user, PLANS]);

  // Reset pagination when switching to My Ads view
  useEffect(() => {
    if (viewMode === 'myads') {
      setAdsToShow(16);
    }
  }, [viewMode]);

  const handleAuthenticate = (userData) => {
    setUser(userData);
    console.log('User authenticated:', userData);
  };

  const handleSelectPlan = async (planData) => {
    console.log('💳 [Subscription] Plan activated:', planData);
    
    // Store subscription data in Supabase
    if (user?.id) {
      const subscription = {
        planId: planData.id,
        startDate: new Date().toISOString(),
        generationsLimit: planData.generations,
        stripeCustomerId: null // Will be populated from Stripe webhook in production
      };
      
      console.log('💾 [Supabase] Saving subscription:', subscription);
      
      const success = await userDataService.updateSubscription(user.id, subscription);
      
      if (success) {
        console.log('✅ [Supabase] Subscription saved');
        
        // Reset generation count for new billing cycle
        setTotalGenerations(0);
        await userDataService.updateGenerationCount(user.id, 0);
      } else {
        console.error('❌ [Supabase] Failed to save subscription');
        alert('Failed to activate subscription. Please try again or contact support.');
        return;
      }
    }
    
    setUserPlan(planData);
    setShowPaymentModal(false);
  };

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    const maxImages = 6; // Limit to 6 images
    
    if (uploadedImages.length + files.length > maxImages) {
      alert(`You can only upload up to ${maxImages} images at a time`);
      return;
    }

    // Convert and compress images to base64
    files.forEach(file => {
      if (!file.type.startsWith('image/')) {
        alert('Please upload only image files');
        return;
      }

      console.log('📷 [Image Upload] Processing:', file.name, 'Size:', (file.size / 1024).toFixed(2), 'KB');

      const reader = new FileReader();
      reader.onloadend = () => {
        // Create an image element to resize/compress
        const img = new Image();
        img.onload = () => {
          console.log('🖼️ [Image Upload] Original dimensions:', img.width, 'x', img.height);
          
          // Resize image if too large (max 2048px on longest side)
          const maxSize = 2048;
          let width = img.width;
          let height = img.height;
          
          if (width > maxSize || height > maxSize) {
            if (width > height) {
              height = (height / width) * maxSize;
              width = maxSize;
            } else {
              width = (width / height) * maxSize;
              height = maxSize;
            }
            console.log('📐 [Image Upload] Resized to:', Math.round(width), 'x', Math.round(height));
          }
          
          // Create canvas and compress
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to JPEG with 85% quality
          const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.85);
          const compressedSize = (compressedDataUrl.length * 0.75 / 1024).toFixed(2);
          console.log('✅ [Image Upload] Compressed size:', compressedSize, 'KB');
          
          setUploadedImages(prev => [...prev, {
            id: `img_${Date.now()}_${Math.random()}`,
            dataUrl: compressedDataUrl,
            name: file.name
          }]);
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeImage = (imageId) => {
    setUploadedImages(prev => prev.filter(img => img.id !== imageId));
  };

  // Detect URLs in text
  const detectURL = (text) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.match(urlRegex);
  };

  // Use Grok to scrape and summarize website (via secure backend)
  const scrapeWebsite = async (url) => {
    console.log('🌐 [Grok Scraper] Starting scrape for:', url);
    console.log('🔑 [Grok Scraper] Using secure backend API');

    console.log('📤 [Grok Scraper] Sending request to backend');

    const response = await fetch('/api/scrape-website', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ url })
    });

    console.log('📥 [Grok Scraper] Response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('❌ [Grok Scraper] API Error:', {
        status: response.status,
        statusText: response.statusText,
        error: errorData
      });
      throw new Error(`Scraping error: ${response.status} - ${errorData.error || 'Unknown error'}`);
    }

    const data = await response.json();
    console.log('✅ [Grok Scraper] Success! Response received');
    console.log('📝 [Grok Scraper] Summary length:', data.choices[0].message.content.length, 'characters');
    console.log('📄 [Grok Scraper] Summary preview:', data.choices[0].message.content.substring(0, 200) + '...');

    return data.choices[0].message.content;
  };

  const generateAds = async () => {
    if (!inputValue.trim() && uploadedImages.length === 0) {
      alert('Please enter a prompt or upload images');
      return;
    }

    // Check if user is authenticated
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    // Check if user has generations remaining (frontend check to prevent unnecessary API calls)
    const generationLimit = userPlan ? userPlan.generations : FREE_LIMIT;
    const remainingGenerations = Math.max(0, generationLimit - totalGenerations);
    
    if (remainingGenerations < 4) { // Need at least 4 generations to create 4 ads
      setShowPaymentModal(true);
      return;
    }

    setIsGenerating(true);

    try {
      console.log('Generating ads with prompt:', inputValue);

      // Check if input contains URLs and scrape them silently in background
      let finalPrompt = inputValue;
      const urls = detectURL(inputValue);
      
      if (urls && urls.length > 0) {
        console.log('\n🔍 [URL Detection] Found URLs:', urls);
        console.log('📊 [URL Detection] Total URLs to scrape:', urls.length);
        
        setIsScrapingUrl(true); // Show scraping status in button
        
        try {
          const scrapeStartTime = Date.now();
          
          // Scrape each URL with Grok (silently in background)
          console.log('🚀 [Scraping Pipeline] Starting parallel scrapes...');
          const scrapedContent = await Promise.all(
            urls.map((url, index) => {
              console.log(`📍 [Scraping Pipeline] Scrape ${index + 1}/${urls.length}: ${url}`);
              return scrapeWebsite(url);
            })
          );
          
          const scrapeEndTime = Date.now();
          const scrapeDuration = ((scrapeEndTime - scrapeStartTime) / 1000).toFixed(2);
          
          console.log(`⏱️  [Scraping Pipeline] All scrapes completed in ${scrapeDuration}s`);
          console.log('📦 [Scraping Pipeline] Total scraped content:', scrapedContent.reduce((acc, content) => acc + content.length, 0), 'characters');
          
          // Combine scraped content with original prompt
          const urlContext = scrapedContent.join('\n\n');
          const originalPromptWithoutUrls = inputValue.replace(/(https?:\/\/[^\s]+)/g, '');
          finalPrompt = `${originalPromptWithoutUrls}\n\nWebsite Context:\n${urlContext}`;
          
          console.log('✅ [Scraping Pipeline] Final prompt prepared');
          console.log('📝 [Scraping Pipeline] Final prompt length:', finalPrompt.length, 'characters');
          console.log('─────────────────────────────────────────\n');
          
          setIsScrapingUrl(false); // Scraping done, now generating images
        } catch (error) {
          console.error('\n❌ [Scraping Pipeline] SCRAPING FAILED');
          console.error('🔴 [Error Details]:', {
            message: error.message,
            stack: error.stack,
            name: error.name
          });
          console.warn('⚠️  [Fallback] Using original prompt without web context');
          console.log('─────────────────────────────────────────\n');
          // If scraping fails, just use the original prompt - don't fail the whole generation
          finalPrompt = inputValue;
          setIsScrapingUrl(false);
        }
      } else {
        console.log('ℹ️  [URL Detection] No URLs detected in prompt');
      }

      // Get Auth0 ID token for API authentication
      const tokenClaims = await getIdTokenClaims();
      const accessToken = tokenClaims.__raw; // Get the raw JWT token

      // Create 4 concurrent requests to OpenRouter
      const generateSingleImage = async (index) => {
        const aspectConfig = ASPECT_RATIOS[selectedAspectRatio];
        const dimensionSpec = `IMPORTANT: Generate this image with EXACTLY ${aspectConfig.width}x${aspectConfig.height} pixels (${aspectConfig.label} aspect ratio). The image MUST be ${aspectConfig.width} pixels wide and ${aspectConfig.height} pixels tall.`;

        // Build content array with text and images
        const content = [];
        
        // Add text prompt (use finalPrompt which may include scraped website content)
        if (finalPrompt.trim()) {
          content.push({
            type: 'text',
            text: uploadedImages.length > 0
              ? `${dimensionSpec}\n\nBased on the provided image(s), ${finalPrompt}. Create a professional advertisement that is visually appealing, creative, and suitable for social media marketing. The composition should work perfectly for ${aspectConfig.description}.`
              : `${dimensionSpec}\n\nGenerate a professional advertisement image for: ${finalPrompt}. Make it visually appealing, creative, and suitable for social media marketing. The composition should work perfectly for ${aspectConfig.description}.`
          });
        } else {
          content.push({
            type: 'text',
            text: `${dimensionSpec}\n\nCreate a professional advertisement based on the provided image(s). Make it visually appealing, creative, and suitable for social media marketing. The composition should work perfectly for ${aspectConfig.description}.`
          });
        }
        
        // Add uploaded images
        uploadedImages.forEach(img => {
          content.push({
            type: 'image_url',
            image_url: {
              url: img.dataUrl
            }
          });
        });

        const response = await fetch('/api/generate-image', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
          },
          body: JSON.stringify({
            model: 'google/gemini-2.5-flash-image',
            messages: [
              {
                role: 'user',
                content: content
              }
            ],
            temperature: 0.7,
            max_tokens: 1000
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));

          // Handle rate limit exceeded
          if (response.status === 429) {
            throw new Error(`RATE_LIMIT:${errorData.message || "You've reached your generation limit. Please upgrade your plan."}`);
          }

          // Handle unauthorized
          if (response.status === 401) {
            throw new Error('UNAUTHORIZED:Please log in again to continue.');
          }

          throw new Error(`API request failed: ${response.status} - ${errorData.error?.message || errorData.message || 'Unknown error'}`);
        }

        const data = await response.json();
        console.log('OpenRouter response:', data);
        console.log('Full choices[0]:', data.choices?.[0]);
        console.log('Message:', data.choices?.[0]?.message);
        
        // Extract base64 image from response - handle multiple possible formats
        let imageUrl = null;
        
        const choice = data.choices?.[0];
        const message = choice?.message;
        
        // Format 1: images array with image_url object
        if (message?.images?.[0]?.image_url?.url) {
          imageUrl = message.images[0].image_url.url;
          console.log('✅ Found image (Format 1):', imageUrl.substring(0, 50) + '...');
        }
        // Format 2: Direct images array with url
        else if (message?.images?.[0]?.url) {
          imageUrl = message.images[0].url;
          console.log('✅ Found image (Format 2):', imageUrl.substring(0, 50) + '...');
        }
        // Format 3: Content field with base64 data
        else if (message?.content) {
          const content = message.content;
          console.log('Content type:', typeof content, 'Starts with data:', content.startsWith?.('data:'));
          
          // Check if content is a base64 data URL
          if (typeof content === 'string' && content.startsWith('data:image')) {
            imageUrl = content;
            console.log('✅ Found image (Format 3 - data URL)');
          } else if (typeof content === 'string' && content.includes('base64,')) {
            imageUrl = content;
            console.log('✅ Found image (Format 3 - base64)');
          } else {
            console.warn('⚠️ Content is not an image, got:', typeof content, content?.substring?.(0, 100));
          }
        }
        // Format 4: Check if there's a url field directly on choice
        else if (choice?.url) {
          imageUrl = choice.url;
          console.log('✅ Found image (Format 4):', imageUrl.substring(0, 50) + '...');
        }
        
        // If still no image found, log everything for debugging
        if (!imageUrl) {
          console.error('❌ NO IMAGE FOUND. Logging all possible locations:');
          console.error('- message?.images:', message?.images);
          console.error('- message?.content:', typeof message?.content, message?.content?.substring?.(0, 100));
          console.error('- choice?.url:', choice?.url);
          console.error('Full choice object:', choice);
        } else {
          console.log('🎨 Final imageUrl set:', imageUrl ? 'YES (length: ' + imageUrl.length + ')' : 'NO');
        }
        
        // Upload to Supabase Storage and get public URL
        const adId = `ad_${Date.now()}_${index}_${Math.random()}`;
        console.log('📤 Uploading image to Supabase Storage...');
        
        const publicUrl = await uploadImageToStorage(imageUrl, adId);
        
        if (publicUrl) {
          console.log('✅ Image uploaded, public URL:', publicUrl);
        } else {
          console.warn('⚠️ Upload failed, using data URL as fallback');
        }
        
        // Generate caption for the image
        let caption = '';
        try {
          console.log('🏷️ Generating caption for image...');
          const captionResponse = await fetch('/api/generate-image', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify({
              model: 'google/gemini-2.5-flash-image',
              messages: [
                {
                  role: 'user',
                  content: [
                    {
                      type: 'text',
                      text: 'Write a short, catchy caption (10-15 words max) for this advertisement image. Just return the caption text, nothing else.'
                    },
                    {
                      type: 'image_url',
                      image_url: {
                        url: publicUrl || imageUrl
                      }
                    }
                  ]
                }
              ],
              temperature: 0.7,
              max_tokens: 50
            })
          });

          if (captionResponse.ok) {
            const captionData = await captionResponse.json();
            caption = captionData.choices?.[0]?.message?.content || '';
            console.log('✅ Caption generated:', caption);
          }
        } catch (error) {
          console.error('Failed to generate caption:', error);
        }
        
        return {
          id: adId,
          prompt: finalPrompt,
          imageUrl: publicUrl || imageUrl, // Use public URL or fallback to data URL
          caption: caption,
          aspectRatio: selectedAspectRatio,
          width: aspectConfig.width,
          height: aspectConfig.height,
          createdAt: new Date().toISOString(),
          model: 'google/gemini-2.5-flash-image'
        };
      };

      // Generate 4 ads concurrently
      const imagePromises = Array.from({ length: 4 }, (_, i) => generateSingleImage(i));
      const newAds = await Promise.all(imagePromises);

      console.log('🎨 Generated ads:', newAds.length, 'ads');
      console.log('🖼️ Images with URLs:', newAds.filter(ad => ad.imageUrl).length);
      newAds.forEach((ad, i) => {
        console.log(`\n📷 Ad ${i + 1}:`);
        console.log('  - ID:', ad.id);
        console.log('  - Has imageUrl:', !!ad.imageUrl);
        console.log('  - URL:', ad.imageUrl);
        console.log('  - URL type:', ad.imageUrl?.startsWith('http') ? 'PUBLIC URL' : ad.imageUrl?.startsWith('data:') ? 'DATA URL' : 'UNKNOWN');
      });

      // Save generated ads to database
      if (user?.id) {
        console.log('💾 [Supabase] Saving', newAds.length, 'ads to history...');
        for (const ad of newAds) {
          await userDataService.saveGeneratedAd(user.id, ad);
        }
        console.log('✅ [Supabase] Ads saved to history');
      }

      // Move current recent ads to history (they're already in generatedAds)
      // Set only new ads as recent
      setRecentGeneratedAds(newAds);
      
      // Add to full history
      setGeneratedAds(prevAds => {
        const updated = [...newAds, ...prevAds];
        console.log('📋 Total ads in state:', updated.length);
        return updated;
      });
      
      // Stay on home view to see results
      setViewMode('home');
      
      setTotalGenerations(prev => prev + 4);
      setInputValue('');
      setUploadedImages([]); // Clear uploaded images after generation
      
    } catch (error) {
      console.error('Error generating ads:', error);

      // Handle rate limit errors
      if (error.message.startsWith('RATE_LIMIT:')) {
        setShowPaymentModal(true); // Show upgrade modal
        return;
      }

      // Handle auth errors
      if (error.message.startsWith('UNAUTHORIZED:')) {
        const message = error.message.replace('UNAUTHORIZED:', '');
        alert(message);
        setShowAuthModal(true); // Show login modal
        return;
      }

      // Handle other errors
      alert(`Failed to generate ads: ${error.message}. Please try again.`);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      generateAds();
    }
  };

  const handleDownload = async (ad) => {
    if (!ad.imageUrl) {
      alert('Image not available for download');
      return;
    }

    try {
      // Fetch the image as a blob
      const response = await fetch(ad.imageUrl);
      const blob = await response.blob();
      
      // Create a blob URL
      const blobUrl = URL.createObjectURL(blob);
      
      // Create a temporary link element
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `aris-ad-${ad.id}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download image. Please try again.');
    }
  };

  const handleEdit = (ad) => {
    if (!ad.imageUrl) {
      alert('Image not available for editing');
      return;
    }
    setEditingAd(ad);
  };

  const handleUpdateGenerations = (count) => {
    setTotalGenerations(prev => prev + count);
  };

  const handleSaveEditedAd = async (originalAdId, editedAd) => {
    // Add edited ad to the recent results (so it shows up in the grid)
    setRecentGeneratedAds(prev => [editedAd, ...prev]);
    
    // Also add to full history
    setGeneratedAds(prev => [editedAd, ...prev]);
    
    // Save to database if user is logged in (with parent relationship)
    if (user?.id) {
      await userDataService.saveGeneratedAd(user.id, editedAd, originalAdId);
    }
  };

  const handleUpdateEditHistory = (adId, history) => {
    setEditHistories(prev => ({
      ...prev,
      [adId]: history
    }));
  };

  const handleDownloadAll = async () => {
    const adsToDownload = viewMode === 'myads' ? generatedAds : recentGeneratedAds;
    
    if (adsToDownload.length === 0) {
      alert('No ads to download');
      return;
    }

    try {
      const zip = new JSZip();
      
      // Add each ad image to the zip
      for (let i = 0; i < adsToDownload.length; i++) {
        const ad = adsToDownload[i];
        if (ad.imageUrl) {
          try {
            // Fetch the image as blob
            const response = await fetch(ad.imageUrl);
            const blob = await response.blob();
            zip.file(`aris-ad-${i + 1}.png`, blob);
          } catch (err) {
            console.error('Failed to fetch image:', ad.imageUrl, err);
          }
        }
      }

      // Generate zip file
      const content = await zip.generateAsync({ type: 'blob' });
      
      // Create download link
      const link = document.createElement('a');
      link.href = URL.createObjectURL(content);
      link.download = `aris-ads-${Date.now()}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Failed to create zip:', error);
      alert('Failed to download all ads. Please try again.');
    }
  };

  const generationLimit = userPlan ? userPlan.generations : FREE_LIMIT;
  const remainingGenerations = Math.max(0, generationLimit - totalGenerations);

  // Automatic slider animation with smooth easing
  useEffect(() => {
    let startTime = null;
    let animationFrame;
    const duration = 3000; // 3 seconds for each direction
    const pauseDuration = 400; // Pause at each end
    let isPaused = false;
    let pauseStart = null;
    let goingForward = true;
    
    const easeInOutCubic = (t) => {
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    };
    
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      
      // Handle pause
      if (isPaused) {
        if (!pauseStart) pauseStart = timestamp;
        if (timestamp - pauseStart >= pauseDuration) {
          isPaused = false;
          pauseStart = null;
          startTime = timestamp;
          goingForward = !goingForward;
        }
        animationFrame = requestAnimationFrame(animate);
        return;
      }
      
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeInOutCubic(progress);
      
      const position = goingForward ? easedProgress * 55 : 55 - (easedProgress * 55);
      setSliderPosition(position);
      
      // Pause at end of cycle
      if (progress >= 1) {
        isPaused = true;
      }
      
      animationFrame = requestAnimationFrame(animate);
    };
    
    animationFrame = requestAnimationFrame(animate);
    
    return () => cancelAnimationFrame(animationFrame);
  }, []);

  // Show blank screen while Auth0 is initializing
  if (isLoading) {
    return (
      <div className="App">
        <StarsBackground />
        <div className="app-content"></div>
      </div>
    );
  }

  return (
    <div className="App">
      <StarsBackground />
      
      <div className="app-content">
        {/* Header */}
        <header className="app-header">
          <div className="logo-container" onClick={() => {
            setViewMode('home');
            setRecentGeneratedAds([]);
          }} style={{ cursor: 'pointer' }}>
            <ArisLogo />
            <div className="brand-name">Aris</div>
          </div>
          
          <nav className="nav-buttons">
            {user ? (
              <>
                {viewMode !== 'myads' && (
                  <button className="nav-button" onClick={() => setViewMode('myads')}>
                    My Ads
                  </button>
                )}
                <a 
                  href="https://calendly.com/bruce-kumar-aris/30min" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="nav-button book-demo-button"
                >
                  Get a demo
                </a>
              </>
            ) : (
              <>
                <button className="nav-button-text" onClick={() => setShowAuthModal(true)}>
                  Sign in
                </button>
                <a 
                  href="https://calendly.com/bruce-kumar-aris/30min" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="nav-button book-demo-button"
                >
                  Get a demo
                </a>
                <button className="nav-button primary start-button" onClick={() => setShowAuthModal(true)}>
                  Start for $0 →
                </button>
              </>
            )}
          </nav>
        </header>

        {/* Main Content */}
        <main className="main-section">
          {/* Landing Page with Two-Column Layout */}
          {viewMode === 'home' && recentGeneratedAds.length === 0 ? (
            <div className="landing-page-wrapper">
              {/* Preview Image - Only on landing page */}
              <div className="landing-preview">
                <img src="/try_again.png" alt="Ad Preview" className="preview-phone-image" />
              </div>

              <div className="landing-content">
                {/* Hero Section */}
            <div className="hero-section">
              <img src="/aris_logo.png" alt="Aris" className="hero-logo" />
                  <h1 className="hero-title" data-text="The AI Admaker">
                Create winning ads
                <br />
                with AI
              </h1>
                </div>

                {/* Chat Input */}
                <div className="chat-input-container">
            <div className="input-wrapper">
              <div className="input-container">
                {!inputValue && uploadedImages.length === 0 && (
                  <div className="typing-placeholder">
                    <TypeAnimation
                      sequence={[
                        'Create an ad for my shoe product',
                        2000,
                        'Design an ad for my restaurant in Palo Alto',
                        2000,
                        'Build a creative ad for my agency',
                        2000,
                        'Make an ad for my coffee shop',
                        2000,
                        'Generate an ad for my tech startup',
                        2000,
                        'Create an ad for my fitness app',
                        2000,
                        'Design an ad for my fashion brand',
                        2000,
                        'Build an ad for my real estate listing',
                        2000,
                        'Make an ad for my online store',
                        2000,
                        'Create an ad for my dental practice',
                        2000,
                      ]}
                      wrapper="span"
                      speed={50}
                      deletionSpeed={80}
                      repeat={Infinity}
                      cursor={true}
                      style={{ display: 'inline-block' }}
                    />
            </div>
          )}

                {/* Image Upload Preview */}
                {uploadedImages.length > 0 && (
                  <div className="uploaded-images-preview">
                    {uploadedImages.map(img => (
                      <div key={img.id} className="preview-image-container">
                        <img src={img.dataUrl} alt={img.name} className="preview-image" />
                        <button 
                          className="remove-image-button" 
                          onClick={() => removeImage(img.id)}
                          type="button"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
              <textarea
                ref={inputRef}
                className="chat-input"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isGenerating}
                  placeholder={uploadedImages.length > 0 ? "Describe what you want to do with these images..." : ""}
              />
              </div>
              
              <div className="input-footer">
                {/* Image Upload Button - Bottom Left */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
                <button
                  className="image-upload-button"
                  onClick={() => fileInputRef.current?.click()}
                  type="button"
                  title="Upload images (max 6)"
                  disabled={isGenerating || uploadedImages.length >= 6}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                    <polyline points="21 15 16 10 5 21"></polyline>
                  </svg>
                  {uploadedImages.length > 0 && <span className="image-count">{uploadedImages.length}/6</span>}
                </button>
                
                <button
                  className={`generate-button ${isGenerating ? 'loading' : ''}`}
                  onClick={generateAds}
                  disabled={isGenerating || (!inputValue.trim() && uploadedImages.length === 0)}
                >
                  {isGenerating ? (
                    <>
                      <span className="loading-dots">
                        <span></span>
                        <span></span>
                        <span></span>
                      </span>
                      {isScrapingUrl ? (
                        'Reading website (~1-2 min)'
                      ) : (
                        LOADING_MESSAGES[loadingMessageIndex]
                      )}
                    </>
                  ) : (
                    <>
                      Generate Ads →
                    </>
                  )}
                </button>
              </div>
            </div>

            {user && (
              <div className="generation-counter">
                <strong>{remainingGenerations}</strong> {userPlan ? `${userPlan.name} plan` : 'free'} generations remaining
                {!userPlan && remainingGenerations <= 10 && remainingGenerations > 0 && (
                  <span> • <span 
                    style={{ color: '#FFA500', cursor: 'pointer' }}
                    onClick={() => setShowPaymentModal(true)}
                  >
                    Upgrade for more
                  </span></span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    ) : (
      /* Non-Landing Page: Regular Layout */
          <div className={`chat-input-container ${recentGeneratedAds.length > 0 || viewMode === 'myads' ? 'has-results' : ''}`}>
            <div className="input-wrapper">
              <div className="input-container">
                {!inputValue && uploadedImages.length === 0 && (
                  <div className="typing-placeholder">
                    <TypeAnimation
                      sequence={[
                        'Create an ad for my shoe product',
                        2000,
                        'Design an ad for my restaurant in Palo Alto',
                        2000,
                        'Build a creative ad for my agency',
                        2000,
                        'Make an ad for my coffee shop',
                        2000,
                        'Generate an ad for my tech startup',
                        2000,
                        'Create an ad for my fitness app',
                        2000,
                        'Design an ad for my fashion brand',
                        2000,
                        'Build an ad for my real estate listing',
                        2000,
                        'Make an ad for my online store',
                        2000,
                        'Create an ad for my dental practice',
                        2000,
                      ]}
                      wrapper="span"
                      speed={50}
                      deletionSpeed={80}
                      repeat={Infinity}
                      cursor={true}
                      style={{ display: 'inline-block' }}
                    />
                  </div>
                )}
                
                {/* Image Upload Preview */}
                {uploadedImages.length > 0 && (
                  <div className="uploaded-images-preview">
                    {uploadedImages.map(img => (
                      <div key={img.id} className="preview-image-container">
                        <img src={img.dataUrl} alt={img.name} className="preview-image" />
                        <button 
                          className="remove-image-button" 
                          onClick={() => removeImage(img.id)}
                          type="button"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
              <textarea
                ref={inputRef}
                className="chat-input"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isGenerating}
                  placeholder={uploadedImages.length > 0 ? "Describe what you want to do with these images..." : ""}
              />
              </div>
              
              <div className="input-footer">
                {/* Image Upload Button - Bottom Left */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
                <button
                  className="image-upload-button"
                  onClick={() => fileInputRef.current?.click()}
                  type="button"
                  title="Upload images (max 6)"
                  disabled={isGenerating || uploadedImages.length >= 6}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                    <polyline points="21 15 16 10 5 21"></polyline>
                  </svg>
                  {uploadedImages.length > 0 && <span className="image-count">{uploadedImages.length}/6</span>}
                </button>
                
                <button
                  className={`generate-button ${isGenerating ? 'loading' : ''}`}
                  onClick={generateAds}
                  disabled={isGenerating || (!inputValue.trim() && uploadedImages.length === 0)}
                >
                  {isGenerating ? (
                    <>
                      <span className="loading-dots">
                        <span></span>
                        <span></span>
                        <span></span>
                      </span>
                      {isScrapingUrl ? (
                        'Reading website (~1-2 min)'
                      ) : (
                        LOADING_MESSAGES[loadingMessageIndex]
                      )}
                    </>
                  ) : (
                    <>
                      Generate Ads →
                    </>
                  )}
                </button>
              </div>
            </div>

            {user && (
              <div className="generation-counter">
                <strong>{remainingGenerations}</strong> {userPlan ? `${userPlan.name} plan` : 'free'} generations remaining
                {!userPlan && remainingGenerations <= 10 && remainingGenerations > 0 && (
                  <span> • <span 
                    style={{ color: '#FFA500', cursor: 'pointer' }}
                    onClick={() => setShowPaymentModal(true)}
                  >
                    Upgrade for more
                  </span></span>
                )}
              </div>
            )}
          </div>
    )}

          {/* Results Section - Show on home when there are recent ads, or show all ads in myads view */}
          {((viewMode === 'home' && recentGeneratedAds.length > 0) || (viewMode === 'myads' && generatedAds.length > 0)) && (
            <div className={`results-section ${viewMode === 'myads' ? 'myads-view' : ''}`}>
              <div className="results-header">
                <button className="download-all-button" onClick={handleDownloadAll} title="Download all ads as ZIP">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="7 10 12 15 17 10"></polyline>
                    <line x1="12" y1="15" x2="12" y2="3"></line>
                  </svg>
                  <span>Download All</span>
                </button>
              </div>
              
              <div className="results-grid">
                {(viewMode === 'myads' ? generatedAds.slice(0, adsToShow) : recentGeneratedAds).map((ad) => (
                  <div key={ad.id} className="ad-result-wrapper">
                    <div className="ad-image-container" onClick={() => handleEdit(ad)}>
                      <img 
                        src={ad.imageUrl} 
                        alt="Generated Ad"
                        className="ad-image"
                        onLoad={(e) => e.target.classList.add('loaded')}
                      />
                      <div className="ad-hover-overlay">
                        <button className="ad-hover-button edit-button" onClick={(e) => { e.stopPropagation(); handleEdit(ad); }}>
                          Edit
                        </button>
                        <button className="ad-hover-button download-icon-button" onClick={(e) => { e.stopPropagation(); handleDownload(ad); }} title="Download">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                            <polyline points="7 10 12 15 17 10"></polyline>
                            <line x1="12" y1="15" x2="12" y2="3"></line>
                          </svg>
                        </button>
                      </div>
                    </div>
                    {ad.caption && (
                      <div className="ad-caption">
                        {ad.caption}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Load More Button - only show in My Ads view when there are more ads */}
              {viewMode === 'myads' && adsToShow < generatedAds.length && (
                <div className="load-more-container">
                  <button 
                    className="load-more-button"
                    onClick={() => setAdsToShow(prev => prev + 16)}
                  >
                    Load More ({generatedAds.length - adsToShow} remaining)
                  </button>
                </div>
              )}

              {/* History Section - only show on home view */}
              {viewMode === 'home' && generatedAds.length > recentGeneratedAds.length && (
                <div className="history-section">
                  <button 
                    className="history-toggle"
                    onClick={() => setShowHistory(!showHistory)}
                  >
                    <svg 
                      width="16" 
                      height="16" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2"
                      style={{ 
                        transform: showHistory ? 'rotate(90deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s ease'
                      }}
                    >
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                    <span>History</span>
                  </button>
                  
                  {showHistory && (
                    <div className="history-grid">
                      {generatedAds.slice(recentGeneratedAds.length).map((ad) => (
                        <div key={ad.id} className="ad-result-wrapper">
                          <div className="ad-image-container" onClick={() => handleEdit(ad)}>
                            <img 
                              src={ad.imageUrl} 
                              alt="Generated Ad"
                              className="ad-image"
                              onLoad={(e) => e.target.classList.add('loaded')}
                            />
                            <div className="ad-hover-overlay">
                              <button className="ad-hover-button edit-button" onClick={(e) => { e.stopPropagation(); handleEdit(ad); }}>
                                Edit
                              </button>
                              <button className="ad-hover-button download-icon-button" onClick={(e) => { e.stopPropagation(); handleDownload(ad); }} title="Download">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                  <polyline points="7 10 12 15 17 10"></polyline>
                                  <line x1="12" y1="15" x2="12" y2="3"></line>
                                </svg>
                              </button>
                            </div>
                          </div>
                          {ad.caption && (
                            <div className="ad-caption">
                              {ad.caption}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </main> 
      </div>

      {/* Modals */}
      {showAuthModal && (
        <AuthModal
          onClose={() => setShowAuthModal(false)}
          onAuthenticate={handleAuthenticate}
        />
      )}

      {showPaymentModal && (
        <PaymentModal
          onClose={() => setShowPaymentModal(false)}
          onSelectPlan={handleSelectPlan}
          currentGenerations={totalGenerations}
          userEmail={user?.email || ''}
        />
      )}

      {editingAd && (
        <AdEditor
          ad={editingAd}
          onClose={() => setEditingAd(null)}
          onUpdateGenerations={handleUpdateGenerations}
          onSaveEditedAd={handleSaveEditedAd}
          editHistory={editHistories[editingAd.id]}
          onUpdateEditHistory={handleUpdateEditHistory}
        />
      )}

      {/* Footer - Only show on homepage when no recent results */}
      {viewMode === 'home' && recentGeneratedAds.length === 0 && (
        <>
          <footer className="app-footer">
            <p className="footer-text">The #1 professional ad tool created by engineers from</p>
            <div className="company-logos">
              <img src="/pinterest_logo.png" alt="Pinterest" className="company-logo pinterest-logo" />
              <img src="/X_logo_2023_(white).png" alt="X" className="company-logo" />
              <img src="/bloomberg_logo.png" alt="Bloomberg" className="company-logo" />
              <img src="/playstation_logo.png" alt="PlayStation" className="company-logo" />
              <img src="/xai_logo-transparent.png" alt="xAI" className="company-logo" />
            </div>
          </footer>

          {/* Before/After Demo Section */}
          <section className="demo-section">
            <div className="before-after-container">
              <div className="demo-header">
                <h2 className="demo-title">Edit Any Ad in Seconds</h2>
                <p className="demo-subtitle">Just describe what you want to change</p>
              </div>
              
              <div className="before-after-wrapper">
                {/* Before Image (underneath) */}
                <div className="before-image-container">
                  <img src="/before.png" alt="Before Edit" className="comparison-image" />
                </div>
                
                {/* After Image (overlay with clip) */}
                <div 
                  className="after-image-container" 
                  style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
                >
                  <img src="/after.png" alt="After Edit" className="comparison-image" />
                  <div className="image-label after-label">After</div>
                </div>
                
                {/* Slider Handle */}
                <div 
                  className="slider-handle" 
                  style={{ left: `${sliderPosition}%` }}
                >
                  <div className="slider-line"></div>
                  <div className="slider-button">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M15 18l-6-6 6-6"/>
                      <path d="M9 6l6 6-6 6"/>
                    </svg>
                  </div>
                </div>
                
              </div>
            </div>
          </section>

          <section className="demo-section">
            <div className="demo-section-content">
              <div className="demo-header animated-header">
                <h2 className="demo-title animated-gradient-text">From Website to Ad in One Click</h2>
                <div className="url-bar-animation">
                  <div className="url-bar">
                    <div className="url-dot"></div>
                    <div className="url-dot"></div>
                    <div className="url-dot"></div>
                    <div className="url-text">https://nike.com</div>
                    <div className="url-arrow">→</div>
                  </div>
                </div>
              </div>
              <img src="/nike.png" alt="Ad Variations Demo" className="demo-image" />
            </div>
          </section>
        </>
      )}

      {/* Discord Link */}
      <a 
        href="https://discord.gg/J7ZDvuw9G5" 
        target="_blank" 
        rel="noopener noreferrer"
        className="discord-link"
        aria-label="Join our Discord"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
        </svg>
        <span>Discord</span>
      </a>

      {/* Copyright Footer */}
      <footer className="copyright-footer">
        <div className="footer-links">
          <a href="/privacy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
          <span className="footer-separator">·</span>
          <a href="/terms.html" target="_blank" rel="noopener noreferrer">Terms of Service</a>
        </div>
        <div className="footer-copyright">
          © {new Date().getFullYear()} Pilot Ventures (Aris). All rights reserved.
        </div>
      </footer>
    </div>
  );
}

// App Router
function App() {
  return (
    <Routes>
      <Route path="/" element={<MainApp />} />
      <Route path="/demo" element={<DemoPage />} />
    </Routes>
  );
}

export default App;
